import { auth } from '@/features/auth/types';
import { firebaseCloudFunctionService } from '@/libs/api';
import { buildUrl, usKidsGolfService } from '@/libs/api/instances/us-kids-golf-instance';
import type { RequestOptions } from '@/libs/api/types';
import { UserRole } from '@/stores';

export const AUTH_ENDPOINTS = {
  LOOKUP_USER_ACCOUNT: '/lookupUserAccount',
  VERIFY_ACCOUNT: '/verifyUserAccount',
  US_KIDS_GOLF_API: {
    GET_ROLES: '/guardians/me/roles',
    GUARDIAN_ME: '/guardians/me',
    GUARDIAN_ONBOARDING: '/guardians/me/onboarding',
    GUARDIAN_GETTING_STARTED: '/guardians/me/getting-started',
    GUARDIAN_GETTING_STARTED_DISMISS: '/guardians/me/getting-started/dismiss',
    GUARDIAN_NOTIFICATION_PREFERENCES: '/guardians/me/notification-preferences',
    GUARDIAN_PLAYERS: '/guardians/me/players',
    GUARDIAN_PLAYER_DETAIL: (playerId: string) => `/guardians/me/players/${playerId}`,
    GUARDIAN_PLAYER_SESSIONS_SUMMARY: (playerId: string) =>
      `/guardians/me/players/${playerId}/sessions/summary`,
    GUARDIAN_PLAYER_SESSIONS: (playerId: string) => `/guardians/me/players/${playerId}/sessions`,
    GUARDIAN_PLAYER_SESSION_DETAIL: (playerId: string, sessionId: string) =>
      `/guardians/me/players/${playerId}/sessions/${sessionId}`,
    GUARDIAN_FAVORITE_COACHES: '/guardians/me/favorite-coaches',
    CREATE_GUARDIAN_PROFILE: '/guardians',
  },
} as const;

export const AuthServices = {
  lookupUserAccount: async (email: string) => {
    const response = await firebaseCloudFunctionService.get<auth.LookupUserAccountResponse>(
      AUTH_ENDPOINTS.LOOKUP_USER_ACCOUNT,
      {
        email,
      },
    );
    return response;
  },

  verifyAccount: async () => {
    const response = await firebaseCloudFunctionService.get<auth.VerifyUserAccountResponse>(
      AUTH_ENDPOINTS.VERIFY_ACCOUNT,
    );
    return response;
  },

  getRoles: async () => {
    const response = await usKidsGolfService.get<{ roles: UserRole[] }>(
      buildUrl(AUTH_ENDPOINTS.US_KIDS_GOLF_API.GET_ROLES),
    );
    return response;
  },

  getGuardianInfo: async (options?: RequestOptions) => {
    const response = await usKidsGolfService.get<auth.GuardianProfileResponse>(
      buildUrl(AUTH_ENDPOINTS.US_KIDS_GOLF_API.GUARDIAN_ME),
      undefined,
      options,
    );
    return response;
  },

  getGuardianPlayers: async (options?: RequestOptions) => {
    const response = await usKidsGolfService.get<auth.GuardianPlayer[]>(
      buildUrl(AUTH_ENDPOINTS.US_KIDS_GOLF_API.GUARDIAN_PLAYERS),
      undefined,
      options,
    );
    return response;
  },

  getGuardianPlayerDetail: async (playerId: string, options?: RequestOptions) => {
    const response = await usKidsGolfService.get<auth.GuardianPlayerDetail>(
      buildUrl(AUTH_ENDPOINTS.US_KIDS_GOLF_API.GUARDIAN_PLAYER_DETAIL(playerId)),
      undefined,
      options,
    );
    return response;
  },

  getGuardianPlayerSessionSummary: async (playerId: string, options?: RequestOptions) => {
    const response = await usKidsGolfService.get<auth.GuardianPlayerSessionSummary>(
      buildUrl(AUTH_ENDPOINTS.US_KIDS_GOLF_API.GUARDIAN_PLAYER_SESSIONS_SUMMARY(playerId)),
      undefined,
      options,
    );
    return response;
  },

  getGuardianPlayerSessions: async (
    playerId: string,
    params?: auth.GuardianPlayerSessionsParams,
    options?: RequestOptions,
  ) => {
    const response = await usKidsGolfService.get<auth.GuardianPlayerSessionsResponse>(
      buildUrl(AUTH_ENDPOINTS.US_KIDS_GOLF_API.GUARDIAN_PLAYER_SESSIONS(playerId)),
      params,
      options,
    );
    return response;
  },

  getGuardianPlayerSessionDetail: async (
    playerId: string,
    sessionId: string,
    options?: RequestOptions,
  ) => {
    const response = await usKidsGolfService.get<auth.GuardianPlayerSessionDetail>(
      buildUrl(AUTH_ENDPOINTS.US_KIDS_GOLF_API.GUARDIAN_PLAYER_SESSION_DETAIL(playerId, sessionId)),
      undefined,
      options,
    );
    return response;
  },

  createGuardianPlayer: async (
    data: auth.CreateGuardianPlayerRequest,
    options?: RequestOptions,
  ) => {
    const response = await usKidsGolfService.post<
      auth.GuardianPlayer,
      auth.CreateGuardianPlayerRequest
    >(buildUrl(AUTH_ENDPOINTS.US_KIDS_GOLF_API.GUARDIAN_PLAYERS), data, options);
    return response;
  },

  updateGuardianPlayer: async (
    playerId: string,
    data: auth.CreateGuardianPlayerRequest,
    options?: RequestOptions,
  ) => {
    const response = await usKidsGolfService.patch<
      auth.GuardianPlayer,
      auth.CreateGuardianPlayerRequest
    >(buildUrl(AUTH_ENDPOINTS.US_KIDS_GOLF_API.GUARDIAN_PLAYER_DETAIL(playerId)), data, options);
    return response;
  },

  deleteGuardianPlayer: async (playerId: string, options?: RequestOptions) => {
    await usKidsGolfService.delete<void>(
      buildUrl(AUTH_ENDPOINTS.US_KIDS_GOLF_API.GUARDIAN_PLAYER_DETAIL(playerId)),
      options,
    );
  },

  syncGuardianInfo: async (
    data: Omit<auth.GuardianProfileRequest, 'email' | 'password'>,
    options?: RequestOptions,
  ) => {
    const response = await usKidsGolfService.put<
      auth.GuardianProfileResponse,
      Omit<auth.GuardianProfileRequest, 'email' | 'password'>
    >(buildUrl(AUTH_ENDPOINTS.US_KIDS_GOLF_API.GUARDIAN_ME), data, options);
    return response;
  },

  updateGuardianInfo: async (data: auth.GuardianProfileUpdateRequest, options?: RequestOptions) => {
    const response = await usKidsGolfService.patch<
      auth.GuardianProfileResponse,
      auth.GuardianProfileUpdateRequest
    >(buildUrl(AUTH_ENDPOINTS.US_KIDS_GOLF_API.GUARDIAN_ME), data, options);
    return response;
  },

  updateOnboardingProgress: async (
    data: auth.UpdateOnboardingRequest,
    options?: RequestOptions,
  ) => {
    const response = await usKidsGolfService.patch<
      auth.GuardianProfileResponse,
      auth.UpdateOnboardingRequest
    >(buildUrl(AUTH_ENDPOINTS.US_KIDS_GOLF_API.GUARDIAN_ONBOARDING), data, options);
    return response;
  },

  /** POST /guardians/me/getting-started — start the Get Started guide (idempotent). */
  startGuardianGettingStarted: async (options?: RequestOptions) => {
    const response = await usKidsGolfService.post<auth.GuardianProfileResponse, undefined>(
      buildUrl(AUTH_ENDPOINTS.US_KIDS_GOLF_API.GUARDIAN_GETTING_STARTED),
      undefined,
      options,
    );
    return response;
  },

  /** PATCH /guardians/me/getting-started — mark a Get Started step done. */
  updateGuardianGettingStarted: async (
    data: auth.UpdateGettingStartedRequest,
    options?: RequestOptions,
  ) => {
    const response = await usKidsGolfService.patch<
      auth.GuardianProfileResponse,
      auth.UpdateGettingStartedRequest
    >(buildUrl(AUTH_ENDPOINTS.US_KIDS_GOLF_API.GUARDIAN_GETTING_STARTED), data, options);
    return response;
  },

  /** PATCH /guardians/me/getting-started/dismiss — close the guide early without finishing. */
  dismissGuardianGettingStarted: async (options?: RequestOptions) => {
    const response = await usKidsGolfService.patch<auth.GuardianProfileResponse, undefined>(
      buildUrl(AUTH_ENDPOINTS.US_KIDS_GOLF_API.GUARDIAN_GETTING_STARTED_DISMISS),
      undefined,
      options,
    );
    return response;
  },

  updateNotificationPreferences: async (
    data: auth.UpdateNotificationPreferencesRequest,
    options?: RequestOptions,
  ) => {
    const response = await usKidsGolfService.patch<
      auth.GuardianProfileResponse,
      auth.UpdateNotificationPreferencesRequest
    >(buildUrl(AUTH_ENDPOINTS.US_KIDS_GOLF_API.GUARDIAN_NOTIFICATION_PREFERENCES), data, options);
    return response;
  },

  addFavoriteCoaches: async (data: auth.AddFavoriteCoachesRequest, options?: RequestOptions) => {
    const response = await usKidsGolfService.post<
      auth.FavoriteCoachResponse[],
      auth.AddFavoriteCoachesRequest
    >(buildUrl(AUTH_ENDPOINTS.US_KIDS_GOLF_API.GUARDIAN_FAVORITE_COACHES), data, options);
    return response;
  },

  createGuardianInfo: async (data: auth.GuardianProfileRequest, options?: RequestOptions) => {
    const response = await usKidsGolfService.post<
      auth.GuardianProfileResponse,
      auth.GuardianProfileRequest
    >(buildUrl(AUTH_ENDPOINTS.US_KIDS_GOLF_API.CREATE_GUARDIAN_PROFILE), data, options);
    return response;
  },
};
